import { useState, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');

    const form = e.currentTarget;
    const description = (form.elements.namedItem('description') as HTMLTextAreaElement)?.value || '';

    if (description.length > 500) {
      setStatus('error');
      return;
    }

    const data = new URLSearchParams();
    const fields = ['nom', 'email', 'telephone', 'description'];
    fields.forEach((field) => {
      const el = form.elements.namedItem(field) as HTMLInputElement | HTMLTextAreaElement | null;
      if (el) data.append(field, el.value);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6ttp3pqcrg2vc5apttg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        formRef.current?.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <main style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Navbar />

      {/* ── Hero ── */}
      <section className="bg-white pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs font-medium tracking-widest text-[#6B6B6B] uppercase">Prise de contact</span>
          <h1 className="text-5xl lg:text-7xl font-bold text-[#0A0A0A] mt-3 leading-tight">Contact</h1>
          <div className="mt-4 w-16 h-0.5 bg-[#0A0A0A]" />
        </div>
      </section>

      {/* ── Main Content ── */}
      <section className="bg-white py-12 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-start">

            {/* Left: Contact Info */}
            <div className="lg:col-span-2">
              <div className="bg-[#F7F6F4] rounded-lg p-8 sticky top-28">
                <h2 className="text-base font-bold text-[#0A0A0A] mb-6 tracking-wide">Informations</h2>

                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold tracking-widest text-[#6B6B6B] uppercase mb-1">Studio</p>
                    <p className="text-sm font-semibold text-[#0A0A0A]">HASPICA</p>
                    <p className="text-sm text-[#6B6B6B]">Patrik Haspica</p>
                    <p className="text-xs text-gray-400 mt-1">SIRET : 102 262 441 00011</p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold tracking-widest text-[#6B6B6B] uppercase mb-2">Adresse</p>
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-5 flex items-center justify-center mt-0.5">
                        <i className="ri-map-pin-line text-[#0A0A0A] text-sm" />
                      </div>
                      <address className="not-italic text-sm text-[#2A2A2A] leading-relaxed">
                        12 Rue Judaïque<br />
                        33000 Bordeaux<br />
                        France
                      </address>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold tracking-widest text-[#6B6B6B] uppercase mb-2">Téléphone</p>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-5 flex items-center justify-center">
                        <i className="ri-phone-line text-[#0A0A0A] text-sm" />
                      </div>
                      <a
                        href="tel:+33607324193"
                        className="text-sm text-[#2A2A2A] hover:text-[#0A0A0A] cursor-pointer transition-colors"
                      >
                        +33 6 07 32 41 93
                      </a>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold tracking-widest text-[#6B6B6B] uppercase mb-2">Email</p>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-5 flex items-center justify-center">
                        <i className="ri-mail-line text-[#0A0A0A] text-sm" />
                      </div>
                      <a
                        href="mailto:patrikhasp1ca@outlook.com"
                        className="text-sm text-[#2A2A2A] hover:text-[#0A0A0A] cursor-pointer transition-colors"
                        rel="nofollow"
                      >
                        patrikhasp1ca@outlook.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <h2 className="text-xl font-bold text-[#0A0A0A] mb-1">Votre projet</h2>
              <p className="text-sm text-[#6B6B6B] mb-8">
                Contactez-nous pour toute demande de projet. Nous vous répondrons dans les plus brefs délais.
              </p>

              {status === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-checkbox-circle-line text-green-600 text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-800">Message envoyé avec succès !</p>
                    <p className="text-sm text-green-600 mt-0.5">Nous vous répondrons dans les plus brefs délais.</p>
                  </div>
                </div>
              )}

              {status === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-error-warning-line text-red-500 text-lg" />
                  </div>
                  <p className="text-sm text-red-700">Une erreur est survenue. Veuillez réessayer ou nous contacter par email.</p>
                </div>
              )}

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-haspica"
                className="space-y-5"
              >
                <div>
                  <label htmlFor="nom" className="block text-xs font-semibold tracking-wide text-[#2A2A2A] uppercase mb-1.5">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    required
                    placeholder="Votre nom et prénom"
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:border-[#0A0A0A] transition-colors bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold tracking-wide text-[#2A2A2A] uppercase mb-1.5">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="votre@email.fr"
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:border-[#0A0A0A] transition-colors bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="telephone" className="block text-xs font-semibold tracking-wide text-[#2A2A2A] uppercase mb-1.5">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    placeholder="+33 6 xx xx xx xx"
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:border-[#0A0A0A] transition-colors bg-white"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-xs font-semibold tracking-wide text-[#2A2A2A] uppercase mb-1.5">
                    Description du projet *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Décrivez votre projet, vos besoins et vos attentes..."
                    className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#0A0A0A] placeholder-gray-400 focus:outline-none focus:border-[#0A0A0A] transition-colors bg-white resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Maximum 500 caractères</p>
                </div>

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full py-4 bg-[#0A0A0A] text-white text-sm font-semibold rounded-md whitespace-nowrap cursor-pointer hover:bg-[#2A2A2A] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {status === 'sending' ? 'Envoi en cours...' : 'Envoyer'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
